<script>
import RawData from "@/components/widgets/raw-data.vue";

export default {
    name: "ProposalItemExpenditure",
    components: {
        RawData,
    },
    props: {
        api: {
            type: Object,
            default: null,
        },
        w: {
            type: Object,
            default: null,
        },
        item: {
            type: Object,
            default: null,
        },
        resources: {
            type: Array,
            default: null,
        },
        resourceLoading: {
            type: Boolean,
            default: null,
        },
        loading: {
            type: Boolean,
            default: null,
        },
        searchItem: {
            type: String,
            default: null,
        },
        viewFrom: {
            type: String,
            default: "planning",
        },
        department: {
            type: [Object, String, Boolean],
            default: null,
        },
    },
    data() {
        return {
            selectedItemIds: [],
        };
    },
    computed: {
        targetApi() {
            return this.api || this.w || this.item || null;
        },
        currentResources() {
            if (this.resources !== null && this.resources !== undefined) {
                return this.resources || [];
            }
            if (this.targetApi && this.targetApi.resources) {
                return this.targetApi.resources;
            }
            return [];
        },
        isLoading() {
            if (this.resourceLoading !== null && this.resourceLoading !== undefined) {
                return this.resourceLoading;
            }
            if (this.loading !== null && this.loading !== undefined) {
                return this.loading;
            }
            if (this.targetApi && this.targetApi.resourceLoading !== undefined) {
                return this.targetApi.resourceLoading;
            }
            return false;
        },
        currentSearchItem() {
            if (this.searchItem !== null && this.searchItem !== undefined) {
                return this.searchItem;
            }
            if (this.targetApi && this.targetApi.searchItem !== undefined) {
                return this.targetApi.searchItem;
            }
            return "";
        },
        displayedItems() {
            return this.filteredItems(this.currentResources, this.currentSearchItem);
        },
        selectedItems() {
            const set = new Set(this.selectedItemIds);
            return (this.currentResources || []).filter((x) => set.has(x.id));
        },
        isAllSelected() {
            return (
                this.displayedItems.length > 0 &&
                this.displayedItems.every((x) => this.selectedItemIds.includes(x.id))
            );
        },
        isIndeterminate() {
            const selectedCount = this.displayedItems.filter((x) =>
                this.selectedItemIds.includes(x.id)
            ).length;
            return selectedCount > 0 && selectedCount < this.displayedItems.length;
        },
    },
    watch: {
        currentResources() {
            const currentIds = new Set((this.currentResources || []).map((x) => x.id));
            this.selectedItemIds = this.selectedItemIds.filter((id) => currentIds.has(id));
        },
        selectedItemIds() {
            this.$emit("selection-change", this.selectedItems);
        },
    },
    methods: {
        filteredItems(items, filter) {
            let data = items || [];
            if (filter && filter.trim() !== "") {
                data = data.filter((x) => {
                    const key = x.filterKey || `${x.itemName || ""} ${x.description || ""}`;
                    return key.toUpperCase().includes(filter.toUpperCase());
                });
            }
            return data;
        },
        changeExpenditure(item) {
            this.$emit("change-expenditure", item);
        },
        toggleSelectAll(event) {
            const isChecked = event.target.checked;
            if (isChecked) {
                const ids = this.displayedItems.map((x) => x.id);
                this.selectedItemIds = Array.from(new Set([...this.selectedItemIds, ...ids]));
            } else {
                const displayedIdSet = new Set(this.displayedItems.map((x) => x.id));
                this.selectedItemIds = this.selectedItemIds.filter((id) => !displayedIdSet.has(id));
            }
        },
        assignFundSource() {
            this.$emit("assign-fund-source", {
                selectedItems: this.selectedItems,
                selectedItem: this.selectedItems,
                targetApi: this.targetApi,
            });
        },
        clearSelection() {
            this.selectedItemIds = [];
        },
    },
};
</script>

<template>
    <table class="table project-list-table table-centered table-borderless align-middle">
        <thead>
            <tr>
                <th scope="col">
                    <div class="d-flex align-items-center ms-2">
                        <div class="form-check form-checkbox-outline form-check-success mb-0 me-2"
                            v-if="viewFrom == 'finance'">
                            <input type="checkbox" class="form-check-input" :checked="isAllSelected"
                                :indeterminate.prop="isIndeterminate"
                                :disabled="!displayedItems || displayedItems.length === 0" @change="toggleSelectAll" />
                        </div>
                        <span>Item of Expenditure</span>
                        <button v-if="selectedItems.length > 0" type="button" class="btn btn-sm btn-primary ms-2"
                            @click="assignFundSource">
                            Assign Fund Source
                        </button>
                    </div>
                </th>
                <th scope="col" width="150px" class="text-center">Quantity</th>
                <th scope="col" width="170px">Unit Cost</th>
                <th scope="col" width="170px">Total</th>
            </tr>
        </thead>
        <tbody>
            <tr v-if="isLoading">
                <td colspan="100%">
                    <div class="d-flex justify-content-center align-items-center">
                        <div class="preloader-component me-2">
                            <div class="status">
                                <div class="spinner-chase w-20px h-20px">
                                    <div class="chase-dot"></div>
                                    <div class="chase-dot"></div>
                                    <div class="chase-dot"></div>
                                    <div class="chase-dot"></div>
                                    <div class="chase-dot"></div>
                                    <div class="chase-dot"></div>
                                </div>
                            </div>
                        </div>
                        <strong>Loading...</strong>
                    </div>
                </td>
            </tr>
            <tr v-if="(!currentResources || currentResources.length == 0) && !isLoading">
                <td colspan="100%" class="text-muted text-center">
                    <i class="dripicons-warning me-1"></i>No data found
                </td>
            </tr>
            <tr v-if="displayedItems.length == 0 && !isLoading && currentResources && currentResources.length > 0">
                <td colspan="100%" class="text-muted text-center">
                    <i class="dripicons-warning me-1"></i>Search not found
                </td>
            </tr>
            <tr v-for="x in displayedItems" :key="x.id" :ref="`resource-tr-${x.id}`">
                <td class="border border-end-0 position-relative">
                    <RawData :data="x"
                        v-if="$store.state.decrypt && $store.state.decrypt.isAdmin && $store.state.decrypt.isAdmin()" />
                    <div class="d-flex align-items-center">
                        <div class="ms-2">
                            <div class="form-check form-checkbox-outline form-check-success mb-0"
                                v-if="viewFrom == 'finance'">
                                <input type="checkbox" class="form-check-input" :id="`selected-${x.id}`" :value="x.id"
                                    v-model="selectedItemIds" />
                                <label class="form-check-label cursor-pointer" :for="`selected-${x.id}`">
                                    {{ x.itemName }}
                                    <span v-if="x.sourceName || (x.fundSource && x.fundSource.sourceName)"
                                        class="badge bg-info text-white ms-2">
                                        Assign to -> {{ x.sourceName || x.fundSource.sourceName }}
                                    </span>
                                </label>
                            </div>
                            <div v-else>
                                <span class="font-weight-bold">{{ x.itemName }}</span>
                                <span v-if="x.fundSource" class="badge bg-info text-white ms-2">
                                    Assign to -> {{ x.fundSource }}
                                </span>
                            </div>
                            <small>{{ x.description }}</small>
                            <small class="d-block text-yellow" v-if="x.note">({{ x.note }})</small>
                            <div>
                                <small>
                                    <i v-if="x.itemCategory">{{ x.itemCategory.name || x.itemCategory }}</i>
                                    <span class="text-warning ms-1">[{{ x.isProcurable ? 'Procurable' : 'Non-procurable'
                                    }}]</span>
                                    <span class="text-warning ms-1" v-if="x.isMixed">[Mixed]</span>
                                    <span class="text-warning ms-1" v-if="x.type">[{{ x.type }}]</span>
                                </small>
                            </div>
                            <div class="d-inline align-items-center mb-1">
                                <span v-if="x.financeTemplateBudgetItem && x.financeTemplateBudgetItem.fundClass"
                                    class="badge bg-purple text-white py-1 px-2 me-2">
                                    {{ x.financeTemplateBudgetItem.fundClass }}
                                </span>
                                <span v-if="x.financeTemplateBudgetItem && x.financeTemplateBudgetItem.accountGroup"
                                    class="badge bg-success text-white py-1 px-2 me-2">
                                    {{ x.financeTemplateBudgetItem.accountGroup }}
                                </span>
                                <span v-if="viewFrom == 'finance'">
                                    <a href="javascript:void(0);" class="text-warning font-size-12 hover-underline"
                                        @click.prevent="changeExpenditure(x)">[
                                        Change Object of Expenditure ]</a>
                                </span>
                            </div>
                        </div>
                    </div>
                </td>
                <td class="border border-start-0 border-end-0 text-center">
                    {{ numberWithCommas(x.totalQuantity) }}
                </td>
                <td class="border border-start-0 border-end-0">
                    {{ formatCurrency(x.inflatedUnitCost) }}
                    <div v-if="x.totalInflation != 0">
                        <small class="d-block">Orig. Cost:
                            <span class="text-warning">{{ formatCurrency(x.unitCost) }}</span>
                        </small>
                        <small class="d-block" v-if="!department">Inflation:
                            {{ x.unitCostWithInflation
                                ? formatCurrency(x.unitCostWithInflation)
                                : formatCurrency(parseFloat(x.inflatedUnitCost) - parseFloat(x.unitCost))
                            }}
                        </small>
                        <small class="d-block" v-else>Inflation:
                            {{ formatCurrency(x.unitCostInflation) }}
                        </small>
                    </div>
                </td>
                <td class="border border-start-0 border-end-0">
                    {{ formatCurrency(parseFloat(x.totalWithInflation)) }}
                    <div v-if="x.totalInflation != 0">
                        <small>Inflation:
                            {{ formatCurrency(x.totalInflation) }}
                        </small>
                    </div>
                </td>
            </tr>
        </tbody>
    </table>
</template>

<style scoped>
.hover-underline:hover {
    text-decoration: underline;
}

.cursor-pointer {
    cursor: pointer;
}
</style>
